import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { Box, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthService } from '../services/api/authService';
import { useAuth } from '../contexts/AuthContext';

interface GoogleCredentialResponse {
  credential: string;
}

interface DecodedCredential {
  email: string;
  name: string;
  picture: string;
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const authService = AuthService.getInstance();

  const handleGoogleLogin = async (credentialResponse: any) => {
    try {
      setLoading(true);
      setError(null);
      
      // Decode the JWT token from Google
      const decoded = jwtDecode<DecodedCredential>(credentialResponse.credential);
      
      // Send to our backend
      const response = await authService.googleLogin(
        credentialResponse.credential,
        decoded.email,
        decoded.name
      );
      
      // Store authentication data
      login(response.token, {
        id: response.userId,
        name: response.name,
        email: response.email,
        organisationId: response.organisationId
      });
      
      // Redirect to home
      navigate('/home');
    } catch (err) {
      console.error('Login failed:', err);
      setError(t('loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '80vh' 
    }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          {t('login')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          gap: 2 
        }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => setError(t('googleLoginFailed'))}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;